import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPost,
  createProject,
  deletePost,
  deleteProject,
  fetchAdminPost,
  fetchAdminPosts,
  fetchAdminProfile,
  fetchAdminProjects,
  fetchDashboard,
  updatePost,
  updateProfile,
  updateProject,
  type AdminPostListParams,
} from '../api/endpoints';
import type { PostPayload, ProjectPayload } from '../api/types';

export function useDashboard() {
  return useQuery({ queryKey: ['admin-dashboard'], queryFn: fetchDashboard });
}

/* ---------------- 文章 ---------------- */

export function useAdminPosts(params: AdminPostListParams) {
  return useQuery({
    queryKey: ['admin-posts', params],
    queryFn: () => fetchAdminPosts(params),
  });
}

export function useAdminPost(id: number | null) {
  return useQuery({
    queryKey: ['admin-post', id],
    queryFn: () => fetchAdminPost(id as number),
    enabled: id !== null,
  });
}

export function useCreatePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PostPayload) => createPost(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useUpdatePost(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<PostPayload>) => updatePost(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-post', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

export function useDeletePost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePost(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-posts'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
  });
}

/* ---------------- 项目 ---------------- */

export function useAdminProjects() {
  return useQuery({
    queryKey: ['admin-projects'],
    queryFn: fetchAdminProjects,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProjectPayload) => createProject(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<ProjectPayload>) => updateProject(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteProject(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

/* ---------------- 个人资料 ---------------- */

export function useAdminProfile() {
  return useQuery({ queryKey: ['admin-profile'], queryFn: fetchAdminProfile });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateProfile,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin-profile'] });
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}
